"use client";

import { useState, useCallback } from "react";
import { toast } from "sonner";
import { SupportedLanguage } from "../types";

interface CodeBlockProps {
  code: string;
  language: SupportedLanguage;
  variant?: "error" | "success" | "neutral";
}

const LANGUAGE_LABELS: Record<SupportedLanguage, string> = {
  javascript: "JavaScript",
  php: "PHP",
  python: "Python",
  java: "Java",
  sql: "SQL",
  other: "Code",
};

export default function CodeBlock({ code, language, variant = "neutral" }: CodeBlockProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    toast.success("Code copied", { duration: 2000 });
    setTimeout(() => setCopied(false), 2000);
  }, [code]);

  const borderColor = {
    error: "border-red-500/20",
    success: "border-emerald-500/20",
    neutral: "border-zinc-700",
  }[variant];

  const headerBg = {
    error: "bg-red-950/30",
    success: "bg-emerald-950/30",
    neutral: "bg-zinc-800/50",
  }[variant];

  return (
    <div className={`rounded-xl border ${borderColor} overflow-hidden`}>
      <div className={`flex items-center justify-between px-4 py-2 ${headerBg} border-b ${borderColor}`}>
        <span
          className="text-xs text-zinc-500 font-medium"
          style={{ fontFamily: "'IBM Plex Mono', monospace" }}
        >
          {LANGUAGE_LABELS[language]}
        </span>
        <button
          onClick={handleCopy}
          className={`flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-md transition-all duration-150 ${
            copied
              ? "text-emerald-400 bg-emerald-500/10" :"text-zinc-500 hover:text-zinc-300 hover:bg-zinc-700/50"
          }`}
        >
          {copied ? (
            <>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              Copied
            </>
          ) : (
            <>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
              </svg>
              Copy
            </>
          )}
        </button>
      </div>
      <pre
        className="p-4 text-sm overflow-x-auto text-zinc-300 leading-6 bg-zinc-950/60"
        style={{ fontFamily: "'IBM Plex Mono', monospace" }}
      >
        <code>{code}</code>
      </pre>
    </div>
  );
}